/**
 * LivePreview — GI card that materializes as the Coach commits fields.
 * Empty → dashed placeholder. As the draft fills in, fields fade in
 * with a subtle rise; when the state machine reaches `ready`, the
 * border solidifies + a single Spark-blue ring pulse fires to mark
 * "done."
 */

import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { DomainBadge } from '@/components/Badge';
import { DomainIcon } from '@/components/DomainIcon';
import { Button } from '@/components/Button';
import type { DomainType } from '@/api/types';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { normalizeDomain } from '@/utils/format';

import type { DraftShape } from './engine';

export function LivePreview({
  draft,
  ready,
  onConfirm,
  onReset,
}: {
  draft: DraftShape | null;
  ready: boolean;
  onConfirm: () => void;
  onReset: () => void;
}) {
  const { t } = useTranslation(['coach', 'common']);
  const reduced = useReducedMotion();

  if (!draft) {
    return (
      <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-border bg-surface p-8 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-raised text-ink-subtle">
          <SkeletonGlyph />
        </div>
        <h3 className="text-sm font-medium text-ink">{t('preview.placeholder_title')}</h3>
        <p className="max-w-xs text-xs text-ink-muted">{t('preview.placeholder_body')}</p>
      </div>
    );
  }

  const normalized = normalizeDomain(draft.domain as DomainType);
  const domainLabel =
    typeof draft.domain === 'string'
      ? t(`domain.${draft.domain}`, { ns: 'common' })
      : draft.domain.Custom;

  const transition = reduced ? { duration: 0 } : { duration: 0.35, ease: 'easeOut' as const };

  return (
    <motion.div
      layout
      initial={false}
      animate={
        ready && !reduced
          ? { boxShadow: ['0 0 0 0 rgb(var(--c-spark) / 0)', '0 0 0 6px rgb(var(--c-spark) / 0.25)', '0 0 0 0 rgb(var(--c-spark) / 0)'] }
          : { boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.04)' }
      }
      transition={reduced ? { duration: 0 } : { duration: 1.2 }}
      className={
        'flex h-full min-h-[320px] flex-col gap-4 rounded-xl border-2 bg-surface p-6 ' +
        (ready ? 'border-spark' : 'border-dashed border-border')
      }
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <span
          className={`flex h-12 w-12 items-center justify-center rounded-lg bg-domain-${normalized}-soft text-domain-${normalized}`}
        >
          <DomainIcon domain={normalized} className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1">
          <DomainBadge domain={normalized} label={domainLabel} />
          <AnimatePresence mode="popLayout">
            {draft.name ? (
              <motion.h3
                key={draft.name}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={transition}
                className="mt-1 text-lg font-semibold text-ink"
              >
                {draft.name}
              </motion.h3>
            ) : null}
          </AnimatePresence>
          <span className="mt-0.5 block font-mono text-[10px] text-ink-subtle">
            v{draft.version}
          </span>
        </div>
      </div>

      {/* Description */}
      <AnimatePresence mode="popLayout">
        {draft.description ? (
          <motion.p
            key={draft.description}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={transition}
            className="text-sm leading-relaxed text-ink-muted"
          >
            {draft.description}
          </motion.p>
        ) : null}
      </AnimatePresence>

      {/* Ports */}
      <PortsBlock title={t('preview.inputs')} ports={draft.inputs.map((p) => p.name)} />
      <PortsBlock title={t('preview.outputs')} ports={draft.outputs.map((p) => p.name)} />

      {/* Parameters */}
      {draft.parameters.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-ink-subtle">
            {t('preview.parameters')}
          </span>
          <ul className="flex flex-wrap gap-2">
            {draft.parameters.map((p) => (
              <li
                key={p.name}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-raised px-2 py-0.5 text-[11px]"
              >
                <span className="font-mono text-ink">{p.name}</span>
                <span className="text-ink-subtle">·</span>
                <span className="text-ink-muted">{formatDefault(p.default)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Confirm CTA — only when the engine signalled ready */}
      {ready ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={reduced ? { duration: 0 } : { duration: 0.35, delay: 0.15 }}
          className="mt-auto flex items-center justify-between gap-2 border-t border-border pt-4"
        >
          <Button variant="ghost" size="sm" onClick={onReset}>
            {t('preview.start_over')}
          </Button>
          <Button variant="primary" size="md" onClick={onConfirm}>
            {t('preview.confirm')}
          </Button>
        </motion.div>
      ) : null}
    </motion.div>
  );
}

function PortsBlock({ title, ports }: { title: string; ports: string[] }) {
  if (ports.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10px] uppercase tracking-wider text-ink-subtle">{title}</span>
      <ul className="flex flex-wrap gap-1.5">
        {ports.map((name) => (
          <li
            key={name}
            className="inline-flex items-center rounded-md border border-border bg-surface-raised px-2 py-0.5 font-mono text-[11px] text-ink"
          >
            {name}
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatDefault(v: DraftShape['parameters'][number]['default']): string {
  if ('integer' in v) return String(v.integer);
  if ('float' in v) return String(v.float);
  if ('bool' in v) return v.bool ? 'true' : 'false';
  if ('string' in v) return JSON.stringify(v.string);
  return v.enum;
}

function SkeletonGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M8 10h8M8 14h5" />
    </svg>
  );
}
